// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import { InternalHeading } from '../../InternalHeading.js';
import { childrenArrayMatcher } from '../../childrenArrayMatcher.js';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import { productColorSunflower } from '../../designSystem.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { useValidationApiOrigin } from '../../validation/ValidationApiOriginContext.js';

export function RSLTNRichTask(props: {
  children: ChildrenArray<Element<typeof RSLTNRichTaskGoogleDoc>>,
  ...CompilerInjectedProps,
}) {
  const { failedValidation } = usePropsValidation(props, RSLTNRichTask);

  if (failedValidation) return null;

  return (
    <>
      <InternalHeading level={3}>Rich task</InternalHeading>
      <p>
        Facilitate this engaging activity so students can activate and apply
        their knowledge
      </p>
      {React.Children.map(props.children, (child, idx) => (
        <p style={{ margin: '8px auto' }} key={idx}>
          {child}
        </p>
      ))}
    </>
  );
}

RSLTNRichTask.spec = {
  children: childrenArrayMatcher({
    min: 1,
    max: Infinity,
    elementType: t.elementType(RSLTNRichTaskGoogleDoc),
  }),
};

export function RSLTNRichTaskGoogleDoc(props: {|
  fileId: string,
  children: string,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLTNRichTaskGoogleDoc,
  );

  const apiOrigin = useValidationApiOrigin();
  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;

    const abortController = new AbortController();

    async function checkFile() {
      try {
        const resp = await fetch(
          `${apiOrigin}/api/google-drive/fileInfo?fileId=${props.fileId}`,
          { signal: abortController.signal },
        );

        if (!resp.ok) {
          addErrors([
            {
              message: `File not found in JSX Content drive`,
              ...locations.fileId,
            },
          ]);
        }

        const fileInfo = await resp.json();

        if (fileInfo.mimeType !== 'application/vnd.google-apps.document')
          addErrors([
            {
              message: `Linked document must be a Google Doc`,
              ...locations.fileId,
            },
          ]);
      } catch (error) {
        if (error.name === 'AbortError') return;
        addErrors([
          {
            message: `An error occurred and file ${props.fileId} was not able to be retrieved`,
            ...locations.fileId,
          },
        ]);
      }
    }

    checkFile();

    return () => abortController.abort();
  });

  return (
    // This odd-looking wrapping span is so that content authors do not need
    // to be sophisticated in the use of {' '} in JSX in order to ensure
    // that here is a space before/after the <RSLTNRichTaskGoogleDoc> component
    // and the surrounding text. This approach should hopefully also allow
    // screen readers to read the paragraph correctly (if we were producing
    // the space using margins, for example, then the adjacent text would be
    // concatenated with the text in here, and so the read-out text would be non-sensical).
    <a
      href={`https://docs.google.com/document/d/${props.fileId}/view`}
      target="_blank"
    >
      {' '}
      <span
        style={{
          display: 'inline-block',
          color: productColorSunflower.eggplant,
          backgroundColor: productColorSunflower.eggplant10,
          borderRadius: 6,
          fontWeight: 600,
        }}
      >
        {/* We use &nbsp; rather than padding so that text selection looks good */}
        &nbsp;{props.children}&nbsp;
      </span>{' '}
    </a>
  );
}

RSLTNRichTaskGoogleDoc.spec = {
  fileId: t.string,
  children: t.string,
};
